import { Button } from '@/components/ui/button';
import { Check, Copy } from 'lucide-react';
import { useRef, useState } from 'react';

/**
 * The secret URL in a read-only monospace field, with « Copier l'URL » (§11.4,
 * planche `ReglagesPartage`).
 *
 * ## `readOnly` and not `disabled`
 *
 * The rule `criteres/` states for a version opened in reading: a disabled
 * control leaves the tab order and its text stops being selectable - which
 * would make the ONE field of this screen whose whole purpose is to be copied
 * the only one that cannot be. It is read-only because §5.1 forbids editing the
 * secret, not because it is inert.
 *
 * ## The copy has a fallback, and the fallback is the field itself
 *
 * `navigator.clipboard` is absent on an insecure origin and can be refused by
 * permission, and this screen is opened over plain HTTP on a dev stack every
 * day. So the button SELECTS the field when the write fails, and says so: the
 * operator finishes with Ctrl+C rather than facing a button that did nothing.
 * A failed copy that looked like a success would be the worst of the three
 * outcomes - the URL is what the whole screen exists to hand over.
 */

export interface LinkUrlFieldProps {
  readonly url: string;
  readonly label: string;
}

export function LinkUrlField({ url, label }: LinkUrlFieldProps) {
  const field = useRef<HTMLInputElement>(null);
  const [state, setState] = useState<'idle' | 'copied' | 'select'>('idle');

  async function copy(): Promise<void> {
    try {
      await navigator.clipboard.writeText(url);
      setState('copied');
    } catch {
      // No clipboard, or refused: hand the operator the selection instead.
      field.current?.select();
      setState('select');
    }
  }

  return (
    <div className="space-y-1">
      <div className="flex items-center gap-2">
        <input
          ref={field}
          type="text"
          readOnly
          aria-label={label}
          data-testid="partage-lien-url"
          value={url}
          onFocus={(event) => event.currentTarget.select()}
          className="h-9 min-w-0 flex-1 rounded-md border-2 border-gray-200 bg-gray-50 px-3 font-mono text-gray-700 text-xs read-only:text-gray-700"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="partage-lien-copier"
          onClick={() => void copy()}
        >
          {state === 'copied' ? (
            <Check size={16} strokeWidth={2} aria-hidden="true" />
          ) : (
            <Copy size={16} strokeWidth={2} aria-hidden="true" />
          )}
          Copier l’URL
        </Button>
      </div>
      {/* Mounted from the first render so the message is announced when it
          arrives, and worded for the outcome that actually happened. */}
      <p aria-live="polite" className="text-gray-600 text-xs">
        {state === 'copied' ? 'URL copiée.' : null}
        {state === 'select' ? 'Le presse-papiers est indisponible : l’URL est sélectionnée.' : null}
      </p>
    </div>
  );
}
